import {
  CASH_ENTRIES,
  ClearwaterAvailableActionTypes,
  ClearwaterTransaction,
  ExternalDataSourceType,
  ExternalTransaction,
  LinkedStatus,
  TRADE_ENTRIES,
  TransactionTypeEnum,
  TransactionTypeKey,
  transactionTypesVal,
} from '../models'

/**
 * Get the transaction type key of a clearwater transaction.
 */
export function getTransactionTypeKey(
  tx: ClearwaterTransaction,
): TransactionTypeKey {
  return (
    transactionTypesVal.find(val => val.code === tx.tranTypeCode)?.type ??
    'UNSUPPORTED'
  )
}
/**
 *Get the transaction type key of a External transaction
 */
export function getTransactionTypeKeyForExt(
  tx: ExternalTransaction,
): TransactionTypeKey {
  return (
    transactionTypesVal.find(val => val.code === tx.tranTypeCode)?.type ??
    'UNSUPPORTED'
  )
}

/**
 * Check if a transaction is a cash entry
 */
export function checkIsCashEntry(tx: ClearwaterTransaction): boolean {
  const txTypeKey = getTransactionTypeKey(tx)
  return CASH_ENTRIES.includes(TransactionTypeEnum[txTypeKey])
}
/**
 *Check if a Selected Transaction is of same category
 */
export function isTransactionSameCategory(
  cwTrans: ClearwaterTransaction,
  extTrans: ExternalTransaction,
): boolean {
  const cwTransTypeKey = getTransactionTypeKey(cwTrans)
  const extTranTypeKey = getTransactionTypeKeyForExt(extTrans)
  const isBothCash =
    CASH_ENTRIES.includes(TransactionTypeEnum[cwTransTypeKey]) &&
    CASH_ENTRIES.includes(TransactionTypeEnum[extTranTypeKey])
  const isBothTrade =
    TRADE_ENTRIES.includes(TransactionTypeEnum[cwTransTypeKey]) &&
    TRADE_ENTRIES.includes(TransactionTypeEnum[extTranTypeKey])

  return isBothCash || isBothTrade
}
/**
 *Check is category of Selected Transaction is Supported for "Match Using External data"
 */
export function isTransactionCategorySupported(
  cwTrans: ClearwaterTransaction,
  extTrans: ExternalTransaction,
): boolean {
  const cwTransTypeKey = getTransactionTypeKey(cwTrans)
  const extTranTypeKey = getTransactionTypeKeyForExt(extTrans)
  if (
    (CASH_ENTRIES.includes(TransactionTypeEnum[extTranTypeKey]) ||
      TRADE_ENTRIES.includes(TransactionTypeEnum[extTranTypeKey])) &&
    (CASH_ENTRIES.includes(TransactionTypeEnum[cwTransTypeKey]) ||
      TRADE_ENTRIES.includes(TransactionTypeEnum[cwTransTypeKey]))
  ) {
    return true
  }
  return false
}

/**
 * Check if a list of transactions are all cash entries.
 */
export function checkAreCashEntries(
  transactions: ClearwaterTransaction[],
): boolean {
  return (
    transactions.length !== 0 && transactions.every(tx => checkIsCashEntry(tx))
  )
}

/**
 * Check if a transaction is a trade entry
 */
export function checkIsTradeEntry(tx: ClearwaterTransaction): boolean {
  const txTypeKey = getTransactionTypeKey(tx)
  return TRADE_ENTRIES.includes(TransactionTypeEnum[txTypeKey])
}

/**
 * Check if a list of transactions are all trade entries.
 */
export function checkAreTradeEntries(
  transactions: ClearwaterTransaction[],
): boolean {
  return (
    transactions.length !== 0 && transactions.every(tx => checkIsTradeEntry(tx))
  )
}

/**
 * Check to if all given transactions have link status equal to the one given.
 */
export function checkAllTransactionsLinkedStatusAllEquals(
  transactions: ClearwaterTransaction[],
  linkedStatus: LinkedStatus,
): boolean {
  return (
    transactions.length > 0 &&
    transactions.every(t => t.linkedStatus === linkedStatus)
  )
}

/**
 * Check to if only some of the given transactions have link status equal to the one given.
 */
export function checkSomeTransactionsLinkedStatusEquals(
  transactions: ClearwaterTransaction[],
  linkedStatus: LinkedStatus,
): boolean {
  return (
    transactions.length > 0 &&
    transactions.some(t => t.linkedStatus === linkedStatus)
  )
}

/**
 * Check to if a transaction's link status is matched.
 */
export function checkIsTransactionMatched(
  transaction: ClearwaterTransaction,
): boolean {
  const linkedStatus = transaction?.linkedStatus
  return linkedStatus !== undefined && linkedStatus !== LinkedStatus.UNMATCHED
}

/**
 * Check if any of the given transactions are already matched.
 */
export function checkAreTransactionsMatched(
  transactions: ClearwaterTransaction[],
): boolean {
  return transactions.some(checkIsTransactionMatched)
}

/**
 * Check if the available actions of a given transaction includes a specific action.
 */
export function checkAvailableActionsIncludes(
  transaction: ClearwaterTransaction,
  action: ClearwaterAvailableActionTypes,
): boolean {
  const availableActions = transaction.availableActions
  return (
    availableActions !== undefined &&
    availableActions.length !== 0 &&
    availableActions.includes(action)
  )
}

/**
 * Check that for a list of a transactions, the available actions for some of the transactions includes a specific action.
 */
export function checkSomeAvailableActionsIncludes(
  transactions: ClearwaterTransaction[],
  action: ClearwaterAvailableActionTypes,
): boolean {
  return (
    transactions.length !== 0 &&
    transactions.some(t => checkAvailableActionsIncludes(t, action))
  )
}

/**
 * Check to see if a clearwater transaction has the same source as an external transaction.
 *
 * +-----------------+-----------------+-----------------+
 * |                 | External Data Source Type         |
 * +-----------------+-----------------+-----------------+
 * | CW LinkedStatus | CUSTODY         | TRADING         |
 * +-----------------+-----------------+-----------------+
 * | UNMATCHED       | OK              | OK              |
 * | CUSTODY_MATCHED | X               | OK              |
 * | TRADING_MATCHED | OK              | X               |
 * | MATCHED         | X               | X               |
 * +-----------------+-----------------+-----------------+
 */
export function checkIsSameSource(
  cwTx: ClearwaterTransaction,
  extTx: ExternalTransaction,
): boolean {
  const isMatched = cwTx.linkedStatus === LinkedStatus.MATCHED
  const areBothCustody =
    cwTx.linkedStatus === LinkedStatus.CUSTODY_MATCHED &&
    extTx.dataSourceType === ExternalDataSourceType.CUSTODY
  const areBothTrading =
    cwTx.linkedStatus === LinkedStatus.TRADING_MATCHED &&
    extTx.dataSourceType === ExternalDataSourceType.TRADING
  return isMatched || areBothCustody || areBothTrading
}
/**
 * if already matched
 *
 * @param cwTx cw tran
 * @param extTx ext tran
 * @returns if already matched
 */
export function checkAlreadyMatched(
  cwTx: ClearwaterTransaction,
  extTx: ExternalTransaction,
): boolean {
  return cwTx.extTranIds?.includes(extTx.extTranId) ?? false
}

/**
 * Returns external data source type from a clearwater transaction, based on linked status.
 * When given linked status is MATCHED or UNMATCHED, then this function returns undefined.
 */
export function getExtDataSourceTypeFromClearwater(
  cwTx: ClearwaterTransaction,
): ExternalDataSourceType | undefined {
  switch (cwTx.linkedStatus) {
    case LinkedStatus.CUSTODY_MATCHED:
      return ExternalDataSourceType.CUSTODY
    case LinkedStatus.TRADING_MATCHED:
      return ExternalDataSourceType.TRADING
    default:
      return undefined
  }
}
